// src/pages/finance-portal/components/StripeLinkButton.js
// Stripe Financial Connections — end-to-end link flow with staged loader.
//
// Stages:
//   1. Create session   → POST /api/finance/stripe/create-session
//   2. Open Stripe modal → stripe.collectFinancialConnectionsAccounts(...)
//   3. Save accounts     → POST /api/finance/stripe/complete-connection
//
// Callers pass either `onConnected` (simple refresh hook) or `onSuccess`
// (legacy, receives session_id + accounts). The button shows a distinct
// label per stage and an inline backdrop overlay during the save step
// so the user can see why the UI froze after Stripe closed.

import React, { useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    Button, CircularProgress, Backdrop, Box, Typography, Alert, Stack,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../../utils/axiosInstance';
import { stripe_publishable_key } from '../../../config';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const stripePromise = stripe_publishable_key
    ? loadStripe(stripe_publishable_key)
    : Promise.resolve(null);

// Stage state machine
const STAGES = {
    IDLE: 'idle',
    CREATING_SESSION: 'creating_session',
    OPENING_STRIPE: 'opening_stripe',
    SAVING: 'saving',
    DONE: 'done',
};

const STAGE_LABELS = {
    [STAGES.IDLE]: null,
    [STAGES.CREATING_SESSION]: 'Starting secure connection…',
    [STAGES.OPENING_STRIPE]: 'Opening Stripe…',
    [STAGES.SAVING]: 'Saving your bank accounts…',
    [STAGES.DONE]: 'Connected!',
};

export default function StripeLinkButton({
    onSuccess,
    onConnected,
    onExit,
    buttonText = 'Connect Bank',
    variant = 'contained',
    fullWidth = false,
    disabled = false,
}) {
    const [stage, setStage] = useState(STAGES.IDLE);
    const [error, setError] = useState(null);

    const busy = stage !== STAGES.IDLE && stage !== STAGES.DONE;
    // Overlay visible during the backend-heavy steps (saving accounts) —
    // Stripe's own modal covers the UI while it's open, so we only show
    // ours when we're making server calls in between.
    const overlayVisible = stage === STAGES.SAVING;

    const reset = useCallback(() => {
        setStage(STAGES.IDLE);
        setError(null);
    }, []);

    const handleConnect = useCallback(async () => {
        setStage(STAGES.CREATING_SESSION);
        setError(null);
        try {
            // 1. Create the Financial Connections session
            const sessRes = await axiosInstance.post('/api/finance/stripe/create-session', {});
            if (!sessRes.data?.success) {
                throw new Error(sessRes.data?.message || 'Failed to start the bank connection');
            }
            const { client_secret, session_id } = sessRes.data;

            // 2. Load Stripe + open the FC modal
            setStage(STAGES.OPENING_STRIPE);
            const stripe = await stripePromise;
            if (!stripe) {
                throw new Error('Stripe library failed to load. Check your publishable key.');
            }

            const result = await stripe.collectFinancialConnectionsAccounts({
                clientSecret: client_secret,
            });

            if (result.error) {
                // User cancelled or Stripe errored. Not a failure to flag loud.
                const msg = result.error.message || 'Connection cancelled';
                setError(msg);
                setStage(STAGES.IDLE);
                if (onExit) onExit(result.error);
                return;
            }

            const linkedAccounts = result.financialConnectionsSession?.accounts || [];
            if (!linkedAccounts.length) {
                setError('No accounts were selected in Stripe. Please try again.');
                setStage(STAGES.IDLE);
                return;
            }

            // 3. Finalize on the backend so StripeConnection + StripeAccount
            //    rows are persisted, balances fetched, and transactions subscribed.
            setStage(STAGES.SAVING);
            const finalRes = await axiosInstance.post(
                '/api/finance/stripe/complete-connection',
                { session_id },
            );
            if (!finalRes.data?.success) {
                throw new Error(finalRes.data?.message || 'Failed to save linked accounts');
            }

            setStage(STAGES.DONE);
            // Legacy callback
            if (onSuccess) {
                onSuccess(session_id, {
                    accounts: linkedAccounts,
                    institution: {
                        name: linkedAccounts[0]?.institution_name || 'Bank',
                    },
                    saved: finalRes.data.accounts || [],
                });
            }
            // Simple refresh hook for pages that just want to reload
            if (onConnected) {
                onConnected(finalRes.data);
            }

            // Drop the "Connected!" label after a beat
            setTimeout(reset, 1500);
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Failed to connect bank';
            setError(msg);
            setStage(STAGES.IDLE);
            if (onExit) onExit({ message: msg });
        }
    }, [onSuccess, onConnected, onExit, reset]);

    const stageLabel = STAGE_LABELS[stage];
    const label = busy
        ? stageLabel || 'Connecting…'
        : stage === STAGES.DONE
            ? 'Connected!'
            : error
                ? 'Retry Connection'
                : buttonText;

    const renderIcon = () => {
        if (busy) return <CircularProgress size={18} color="inherit" />;
        if (stage === STAGES.DONE) return <CheckCircleIcon />;
        return <AccountBalanceIcon />;
    };

    return (
        <>
            <Stack spacing={1} sx={{ width: fullWidth ? '100%' : 'auto' }}>
                <Button
                    variant={variant}
                    onClick={handleConnect}
                    disabled={disabled || busy}
                    fullWidth={fullWidth}
                    startIcon={renderIcon()}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '12px',
                        px: 3,
                        py: 1.25,
                        fontSize: '0.9rem',
                        bgcolor: variant === 'contained'
                            ? (stage === STAGES.DONE ? '#16a34a' : ACCENT)
                            : 'transparent',
                        color: variant === 'contained' ? '#fff' : ACCENT,
                        border: variant === 'outlined' ? `2px solid ${ACCENT}` : 'none',
                        '&:hover': {
                            bgcolor: variant === 'contained' ? '#16a34a' : 'rgba(34,197,94,0.08)',
                        },
                        '&:disabled': {
                            bgcolor: variant === 'contained' ? '#cbd5e1' : 'transparent',
                            color: variant === 'contained' ? '#fff' : '#94a3b8',
                            border: variant === 'outlined' ? '2px solid #cbd5e1' : 'none',
                            opacity: 0.9,
                        },
                    }}
                >
                    {label}
                </Button>
                {error && (
                    <Alert severity="warning" onClose={() => setError(null)}
                           sx={{ py: 0, fontFamily, fontSize: '0.8rem' }}>
                        {error}
                    </Alert>
                )}
            </Stack>

            {/* Full-screen overlay during the backend finalize step —
                prevents the user from thinking the UI froze after Stripe
                closes and before the accounts materialise. */}
            <Backdrop
                open={overlayVisible}
                sx={{
                    zIndex: (t) => t.zIndex.modal + 10,
                    color: '#fff',
                    backdropFilter: 'blur(3px)',
                    bgcolor: 'rgba(15,23,42,0.6)',
                }}
            >
                <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 2, p: 4, borderRadius: 2, bgcolor: '#fff',
                    boxShadow: 6, minWidth: 300, maxWidth: 400,
                }}>
                    <CircularProgress sx={{ color: ACCENT }} size={40} />
                    <Typography variant="body1" sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                        Saving your accounts
                    </Typography>
                    <Typography variant="caption" color="text.secondary"
                                sx={{ fontFamily, textAlign: 'center' }}>
                        Linking institution, importing balances, and subscribing to
                        transaction updates. This usually takes a few seconds.
                    </Typography>
                </Box>
            </Backdrop>
        </>
    );
}
